import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import backgroundBenchmark from "@images/backgroundBenchmark.png"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { CheckPlaneIcon } from "@images/svgIcons/status/checkPlaneIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { useBox } from "@modules/box/hooks/useBox"
import { BoxStaffInvitationErrorView } from "@modules/boxStaff/components/boxStaffInvitationErrorView"
import { displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { BoxStaffActionRequest, BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffInvitation } from "@modules/boxStaff/hooks/useBoxStaff"
import { useBoxStaffInvitationDetails } from "@modules/boxStaff/hooks/useBoxStaffInvitationDetails"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { ExploreCard } from "@modules/explore/components/exploreCard/exploreCard"
import { ExploreBoxItemEntity } from "@modules/explore/entities/exploreEntity"
import { RouteNavigate } from "@modules/navigation/hooks/useBaseNavigate"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { HuPermissions } from "@modules/ui/components/huPermissions"
import { delay } from "@modules/utils/utils"
import { useQueryClient } from "@tanstack/react-query"

export function BoxStaffInvitation({ className, ...props }: React.ComponentProps<"div">) {
  const user = useUser()
  const { hideSidebar } = useSidebar()
  const { localStorageService } = useServicesContext()
  const { responseBoxStaffInvitation, isPending } = useBoxStaffInvitation()
  const { value: boxId } = localStorageService.load(LocalStorageKeys.boxStaffInvitation.boxId, { value: "" })
  const { result: box } = useBox(boxId)
  const boxNavigate = useBoxNavigate()
  const queryClient = useQueryClient()
  const { setTheme } = useTheme()
  const { data: invitationDetails, isLoading } = useBoxStaffInvitationDetails(boxId)

  const invitationStatus = invitationDetails?.status
  const invitationRole = invitationDetails?.role

  const requestBoxStaffInvitation = async (action: BoxStaffActionRequest) => {
    if (user?.id && boxId != "") {
      await responseBoxStaffInvitation({ boxId, action })
        .then(async () => {
          await queryClient.invalidateQueries({ queryKey: ["boxs"] })

          if (action === BoxStaffActionRequest.ACCEPT) {
            toast.success(word("box.staff.invitation.toast.success"), {
              classNames: successToastClassName(),
            })
            hideSidebar()
            setTheme(Theme.OwnerDark)

            await delay(100)
            boxNavigate(RouteNavigate.Root, { newBoxId: boxId })
          } else if (action === BoxStaffActionRequest.REFUSE) {
            toast.success(word("box.staff.invitation.toast.refuse"), {
              classNames: successToastClassName(),
            })
            hideSidebar()
          }
        })
        .catch(() => {
          toast.error(word("box.staff.invitation.toast.error"), {
            classNames: errorToastClassName(),
          })
        })
        .finally(() => {
          localStorage.removeItem(LocalStorageKeys.boxStaffInvitation.boxId)
        })
    }
  }

  return (
    <div className={cn("pb-6", className)} {...props}>
      {/* HEADER */}
      <div className="relative flex h-60 overflow-hidden rounded-b-3xl bg-neutral-200">
        <div className="mt-2 ml-4 flex h-full w-1/2 flex-col items-center justify-center">
          <div
            className="shrink-0 bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          {!box || isLoading ? (
            <>
              <Skeleton className="mt-1 h-8 w-[80%] rounded-xl" />
              <Skeleton className="mt-4 h-4 w-[40%] rounded-xl" />
            </>
          ) : invitationDetails ? (
            <div className="mt-1 flex w-full flex-col items-center justify-center gap-1">
              <Text fontStyle="HM" className="line-clamp-1 text-textDefault-heading">
                {box.name}
              </Text>
              <Text fontStyle="BS" className="text-textDefault-body">
                {word("box.staff.invitation.header.subtitle")}
              </Text>

              <div className="mx-10 flex w-full flex-row items-center justify-center gap-2">
                {invitationDetails.inviter ? (
                  invitationDetails.inviter.firstname &&
                  invitationDetails.inviter.lastname &&
                  `${invitationDetails.inviter.firstname} ${invitationDetails.inviter.lastname}`.length > 21 ? (
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <div className="w-full">
                            <UserCell
                              member={invitationDetails.inviter}
                              subtitle={"@" + invitationDetails.inviter.username}
                              className="mt-1 flex w-full gap-2"
                            />
                          </div>
                        }
                      />
                      <TooltipContent side="bottom" className="max-w-50">
                        <div className="flex flex-col gap-1">
                          <Text fontStyle="LL" className="wrap-break-word">
                            {invitationDetails.inviter.firstname} {invitationDetails.inviter.lastname}
                          </Text>
                          <Text fontStyle="LS" className="wrap-break-word">
                            {"@" + invitationDetails.inviter.username}
                          </Text>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <UserCell
                      member={invitationDetails.inviter}
                      subtitle={"@" + invitationDetails.inviter.username}
                      className="mt-1 flex w-full gap-2"
                    />
                  )
                ) : (
                  <UserCell
                    member={box.author}
                    subtitle={"@" + box.author.username}
                    className="mt-1 flex w-full gap-2"
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div
          className="h-full w-1/2 bg-cover bg-right bg-no-repeat"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>

      {/* CONTENT */}
      {user && box && invitationStatus && invitationRole && (
        <div className="px-12">
          {/* TEXT */}
          <div className="mt-8 flex w-full flex-col items-center justify-center text-center">
            <>
              {invitationStatus === BoxStaffStatus.PENDING ? (
                <Text fontStyle="TL" className="text-textDefault-caption">
                  {word("box.staff.invitation.header.pending", {
                    box: box.name ?? "",
                    role: invitationRole ? displayRole(invitationRole) : "",
                  })}
                </Text>
              ) : (
                <div className="flex flex-row items-center justify-center gap-2">
                  <CheckPlaneIcon color="currentColor" className="text-white" size={24} />
                  <Text fontStyle="TM" className="text-textDefault-caption">
                    {word("box.staff.invitation.header.accepted")}
                  </Text>
                </div>
              )}

              <Text fontStyle="BM" className="my-6 px-4 text-textDefault-caption">
                {invitationStatus === BoxStaffStatus.PENDING
                  ? word("box.staff.invitation.subHeader.pending", {
                      box: box.name ?? "",
                      role: invitationRole ? displayRole(invitationRole) : "",
                    })
                  : word("box.staff.invitation.subHeader.accepted")}
              </Text>
            </>
          </div>
          {/* BOX */}
          <div className="mt-6 flex flex-col">
            <Text className="text-textDefault-caption" fontStyle="LS">
              {word("box.staff.invitation.relevantGym").toUpperCase()}
            </Text>
            <div className="mt-2 flex flex-row items-center justify-center">
              {!box ? (
                <Skeleton className="mt-1 h-75 w-80 rounded-4xl" />
              ) : (
                <ExploreCard
                  item={box as ExploreBoxItemEntity}
                  disabled
                  onViewDetailsClick={() => {
                    return
                  }}
                  isSelected={false}
                />
              )}
            </div>
          </div>
          {/* PERMISSIONS  */}
          <div className="mt-6 flex flex-col">
            <HuPermissions title={word("invitationDetailsInfo.permissions").toUpperCase()} role={invitationRole} />
          </div>
          {/* BUTTONS */}
          {invitationStatus === BoxStaffStatus.PENDING && (
            <div className="flex w-full flex-row items-center justify-between gap-4 py-6">
              <Button
                variant="outline"
                size="md"
                onClick={() => void requestBoxStaffInvitation(BoxStaffActionRequest.REFUSE)}
                disabled={!box || isPending}
              >
                {word("invitationDetailsInfo.refuse")}
                <ErrorIcon color="currentColor" className="text-white" width={20} height={20} />
              </Button>

              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
                }}
                disabled={!box || isPending}
              >
                {word("invitationDetailsInfo.answerLater")}
                <ClockOutlineIcon color="currentColor" className="text-textDefault-body" width={14} height={14} />
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void requestBoxStaffInvitation(BoxStaffActionRequest.ACCEPT)}
                disabled={!box || isPending}
              >
                {word("invitationDetailsInfo.accept")}
                <CorrectIcon color="currentColor" className="text-white" width={20} height={20} />
              </Button>
            </div>
          )}
        </div>
      )}
      {/* LOADING */}
      {!user && !box && (
        <>
          <Skeleton className="mt-1 h-140 w-full rounded-xl" />
        </>
      )}
      {/* ERROR */}
      {user && box && !invitationStatus && <BoxStaffInvitationErrorView />}
    </div>
  )
}
